import { Link } from "react-router-dom";
import {
  Briefcase, GraduationCap, Cpu, BrainCircuit, TrendingUp, Award, Building2, ArrowRight, ArrowLeft,
} from "lucide-react";
import { useLanguage } from "@/i18n/LanguageContext";
import { IMAGES } from "@/lib/images";
import { Reveal, SectionHeading } from "@/components/ui-bits";

const ICONS = [Briefcase, GraduationCap, Cpu, BrainCircuit, TrendingUp, Award, Building2];
const IMGS = [IMAGES.boardroom, IMAGES.workshop, IMAGES.data, IMAGES.ai, IMAGES.strategy, IMAGES.keynote, IMAGES.executive];

export default function Services() {
  const { t, isRTL } = useLanguage();
  const Arrow = isRTL ? ArrowLeft : ArrowRight;

  return (
    <div data-testid="services-page">
      <section className="hero-grain relative overflow-hidden bg-navy-950 py-24 lg:py-32">
        <img src={IMAGES.boardroom} alt="" className="absolute inset-0 h-full w-full object-cover opacity-20" />
        <div className="absolute inset-0 bg-gradient-to-b from-navy-950/50 to-navy-950" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-12">
          <Reveal>
            <p className="mb-4 text-xs font-bold uppercase tracking-[0.3em] text-gold">{t("services.eyebrow")}</p>
            <h1 className="max-w-3xl font-display text-4xl font-semibold text-white sm:text-5xl lg:text-6xl">{t("page.servicesTitle")}</h1>
            <p className="mt-5 max-w-2xl text-base text-slate-300 sm:text-lg">{t("page.servicesSub")}</p>
          </Reveal>
        </div>
      </section>

      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-7xl space-y-16 px-4 sm:px-6 lg:px-12">
          {ICONS.map((Icon, i) => (
            <Reveal key={i}>
              <div
                data-testid={`service-row-${i + 1}`}
                className={`grid items-center gap-10 lg:grid-cols-2 ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}
              >
                <div className="overflow-hidden rounded-lg shadow-xl">
                  <img src={IMGS[i]} alt={t(`s${i + 1}.title`)} className="h-72 w-full object-cover transition-transform duration-700 hover:scale-105" loading="lazy" />
                </div>
                <div>
                  <span className="inline-flex h-12 w-12 items-center justify-center rounded-full bg-gold-soft text-gold-hover">
                    <Icon className="h-5 w-5" />
                  </span>
                  <h2 className="mt-5 font-display text-3xl font-semibold text-navy">{t(`s${i + 1}.title`)}</h2>
                  <p className="mt-4 max-w-xl text-base leading-relaxed text-slate-600">{t(`s${i + 1}.desc`)}</p>
                  <Link
                    to="/consultation"
                    data-testid={`service-consult-cta-${i + 1}`}
                    className="mt-6 inline-flex items-center gap-2 text-sm font-bold uppercase tracking-wider text-gold-hover hover:text-navy"
                  >
                    {t("nav.book")} <Arrow className="h-4 w-4" />
                  </Link>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="bg-navy-950 py-16">
        <div className="mx-auto max-w-4xl px-4 text-center">
          <Reveal>
            <h2 className="font-display text-3xl font-semibold text-white sm:text-4xl">{t("cta.title")}</h2>
            <Link
              to="/consultation"
              data-testid="services-bottom-cta"
              className="mt-8 inline-block rounded-full bg-gold px-9 py-4 text-sm font-bold text-navy transition-colors hover:bg-gold-hover"
            >
              {t("cta.button")}
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
